import moment from "moment";
import { useTranslation } from "react-i18next";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import BandTag from "./BandTag";
import daysUntil from "./daysUntil";
import peopleNouns from "./peopleNouns";

/**
 * Requirements §5.7's consequence for Attention. Ordered by workouts
 * completed ascending, not by expiry: what a trialist did during the trial
 * is the only thing in the funnel a coach can still influence, and
 * conversion runs 0% → 22% → 82% across the workout bands.
 *
 * Rows flagged `unlikelyToConvert` (no workouts AND no card on file) are
 * de-emphasised rather than hidden — they are real, they just are not
 * where the coach's next hour should go.
 */
const TrialsEndingTable = ({
  rows = [],
  context,
  theme = "dark",
  controls = null,
}) => {
  const { t } = useTranslation("coaching-portal");

  return (
    <>
      <header className="table-header">
        <hgroup>
          <div className="flex items-center gap-2">
            <h3 className="title">{t("income.attention.trialsTableTitle")}</h3>
            <BandTag kind="snapshot" label={t("income.bands.now")} />
          </div>
          <p className="p2 text-sm">{t("income.attention.trialsTableBlurb")}</p>
        </hgroup>
        {controls}
      </header>
      {/* biome-ignore lint/a11y/useValidAriaRole: custom role used for styling */}
      <table role="report-table" className="trials-ending-table">
        {/* biome-ignore lint/a11y/useValidAriaRole: custom role used for styling */}
        <thead role="table-header">
          <tr>
            <th>{t(peopleNouns(context).column)}</th>
            <th>{t("income.attention.workoutsCompleted")}</th>
            <th>{t("income.attention.cardOnFile")}</th>
            <th>{t("income.attention.trialEnds")}</th>
            <th>{t("income.attention.daysRemaining")}</th>
          </tr>
        </thead>
        {rows.length > 0 ? (
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.subscriptionId}
                className={row.unlikelyToConvert ? "row-muted" : ""}
              >
                <td>
                  <div className="flex items-center gap-2">
                    <div className="image-wrapper">
                      {row.customerName ? (
                        <img
                          src={getAvatarUrl(
                            { name: row.customerName, avatar: row.avatar },
                            null,
                            theme
                          )}
                          className="w-8 h-8 rounded-full"
                          alt={t("income.avatarAlt")}
                        />
                      ) : (
                        <img
                          style={{ width: 32, height: 32, borderRadius: 32 }}
                          src="/img/avatars/registration-pending2.svg"
                          className="icon-grey"
                          alt=""
                        />
                      )}
                    </div>
                    <span className="subscriber-text">
                      {row.customerName || row.customerEmail || row.customerId}
                    </span>
                    {row.unlikelyToConvert && (
                      <span className="pill text-(--white-30)">
                        {t("income.attention.unlikelyToConvert")}
                      </span>
                    )}
                    {row.cancelAtPeriodEnd && (
                      <span className="pill text-(--white-30)">
                        {t("income.attention.cancelAtPeriodEnd")}
                      </span>
                    )}
                  </div>
                </td>
                <td>{row.workoutsCompleted}</td>
                <td>
                  {row.hasCardOnFile
                    ? t("income.attention.cardYes")
                    : t("income.attention.cardNo")}
                </td>
                <td>
                  {row.trialEnd
                    ? moment(row.trialEnd).format("MMM D, YYYY")
                    : "—"}
                </td>
                <td>
                  {daysUntil(row.trialEnd) == null
                    ? "—"
                    : daysUntil(row.trialEnd) === 0
                      ? t("income.attention.endsToday")
                      : t("income.attention.inDays", {
                          count: daysUntil(row.trialEnd),
                        })}
                </td>
              </tr>
            ))}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td colSpan="100%">
                <div className="body-table-empty">
                  <p>{t("income.attention.noTrialsEnding")}</p>
                </div>
              </td>
            </tr>
          </tbody>
        )}
      </table>
    </>
  );
};

export default TrialsEndingTable;
